/**
 * Reset scroll to the top on a client-side route change.
 *
 * A `<BrowserRouter>` navigation is a `history.pushState` — the document never
 * changes, so the browser keeps the scroll offset it already had. Clicking a
 * footer link at y=1300 therefore landed you 1300px down the NEW page, which
 * reads as "the link goes to the middle of the page". Every site mounted by
 * `mountClient` had this; it was reproduced on fuguelab.com (/about → /services
 * both at y=1301.5).
 *
 * react-router's own `<ScrollRestoration>` is not an option here: it requires a
 * data router (`createBrowserRouter`), and these sites all use the declarative
 * `<BrowserRouter>` + `<Routes>` form.
 *
 * Three cases are deliberately left alone:
 *
 * - **POP** (back/forward). The browser restores the previous offset for a
 *   history entry, and that restore is the correct behaviour — overriding it
 *   would send you to the top of a page you were reading halfway down.
 * - **A hash target.** `/services/done-for-you#concierge` wants the anchor, not
 *   the top. That path already works (the owning page scrolls to it on mount,
 *   and it was verified on prod landing the target 96px down, clear of the 64px
 *   sticky header); jumping to the top first would fight it.
 * - **Same pathname.** A search-param-only change (filters, pagination state)
 *   is not a page change, and yanking the user to the top mid-interaction is a
 *   worse bug than the one being fixed.
 *
 * `behavior: 'instant'` is deliberate. Studio sets `scroll-behavior: smooth` on
 * the root for anchor jumps, and per CSSOM View a scroll sent with `'auto'` —
 * the default, and what the two-argument `scrollTo(0, 0)` sends — resolves to
 * that CSS value. A route change should not animate: a ~0.5s glide to the top
 * would run underneath the 0.35s page transition and delay the new page's first
 * readable paint. `'instant'` is the one value spec'd to ignore the CSS
 * property, so the reset is immediate and `scroll-behavior: smooth` stays intact
 * for the anchor jumps that do want it.
 *
 * `instant` is a ScrollBehavior enum value, so engines predating ~2022 reject the
 * whole dictionary with a TypeError. Uncaught in an effect that would break
 * navigation, hence the guard; the fallback degrades to the old do-nothing
 * behaviour on those browsers rather than taking the page down.
 *
 * ---
 *
 * A measurement caveat, recorded because it wasted a cycle and produced a
 * confidently wrong bug report:
 *
 * Chrome pauses requestAnimationFrame in a background tab, and smooth scrolling
 * is rAF-driven — so in a hidden tab EVERY smooth scroll silently does nothing
 * and `window.scrollY` never moves. Browser automation often drives a hidden
 * tab. Measured that way, `behavior: 'auto'` looked broken here, in-page anchor
 * links looked broken sitewide, and both conclusions were artefacts.
 *
 * Before believing any scroll measurement, assert the environment can animate:
 *
 *   let frames = 0
 *   const stop = performance.now() + 500
 *   const tick = () => { frames++; if (performance.now() < stop) requestAnimationFrame(tick) }
 *   requestAnimationFrame(tick)
 *   // ~60 after 500ms in a visible tab; 0 in a hidden one — where smooth scroll
 *   // cannot work and any "it didn't scroll" result is meaningless.
 *
 * Instant scrolls are synchronous and DO measure correctly in a hidden tab,
 * which is why the bug this file fixes, and this fix, both verified honestly
 * there. Anything involving `smooth` did not.
 */
import { useEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

export function ScrollReset() {
  const { pathname, hash } = useLocation()
  const navigationType = useNavigationType()
  // The pathname at the previous commit. Seeded on mount so the very first
  // effect (hydration) is a no-op — a deep link that loaded scrolled, or a
  // browser restoring a reload offset, must not be stomped.
  const previous = useRef<string | null>(null)

  useEffect(() => {
    const changed = previous.current !== null && previous.current !== pathname
    previous.current = pathname

    if (!changed) return
    if (navigationType === 'POP') return
    if (hash) return

    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    } catch {
      window.scrollTo(0, 0)
    }
  }, [pathname, hash, navigationType])

  return null
}
